import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth, API, saveSessionToken } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import RegistrationModal from '../components/RegistrationModal';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useTranslation } from '../i18n';
import HeroSection from './landing/HeroSection';
import LandingVitalPassWalkthrough from './landing/LandingVitalPassWalkthrough';
import StatsSection from './landing/StatsSection';
import FooterSection from './landing/FooterSection';
import {
  Activity, 
  Building2, 
  Users, 
  TrendingUp, 
  Shield, 
  BarChart3,
  CheckCircle2,
  ArrowRight,
  KeyRound,
  Loader2,
  Mail,
  Lock,
  User,
  Eye,
  EyeOff,
  Menu,
  X,
  Dumbbell,
  Heart,
  Zap,
  Star,
  Crown,
  Flame,
  DollarSign,
  ChevronRight
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { t } = useTranslation();
  const [showStudentLogin, setShowStudentLogin] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const skipRedirectRef = React.useRef(false);
  const [accessCode, setAccessCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [knowSection, setKnowSection] = useState(null);
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Intro video config — fetched from backend (managed by Admin panel).
  // Track page visit
  useEffect(() => {
    axios.post(`${API}/track/visit`, { page: '/', referrer: document.referrer }).catch(() => {});
  }, []);

  // Show registration modal after 3 minutes on landing page
  const promptTimerRef = useRef(null);
  useEffect(() => {
    if (user) return; // Don't prompt logged-in users
    promptTimerRef.current = setTimeout(() => {
      if (!showAuthModal && !showRegistrationModal && !showStudentLogin) {
        setShowRegistrationModal(true);
      }
    }, 3 * 60 * 1000); // 3 minutes
    return () => clearTimeout(promptTimerRef.current);
  }, [user, showAuthModal, showRegistrationModal, showStudentLogin]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUnverifiedEmail(null);
    try {
      const response = await axios.post(
        `${API}/auth/login`,
        { email: formData.email, password: formData.password }
      );
      if (response.data.session_token) saveSessionToken(response.data.session_token);
      setUser(response.data);
      toast.success('Login realizado com sucesso!');
      setShowAuthModal(false);
      if (response.data.role) {
        const dashboardMap = { rh: '/rh', gym: '/gym', student: '/student', professional: '/professional' };
        navigate(dashboardMap[response.data.role]);
      } else {
        navigate('/select-role');
      }
    } catch (error) {
      if (error.response?.status === 403) {
        setUnverifiedEmail(formData.email);
      } else {
        toast.error(error.response?.data?.detail || 'Erro ao processar. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedEmail) return;
    setResendingVerification(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: unverifiedEmail });
      toast.success('E-mail de verificação reenviado! Verifique sua caixa de entrada.');
    } catch {
      toast.error('Erro ao reenviar e-mail. Tente novamente.');
    } finally {
      setResendingVerification(false);
    }
  };

  const handleStudentLogin = async (e) => {
    e.preventDefault();
    if (!accessCode.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/auth/student-login`,
        { access_code: accessCode.trim() }
      );
      if (response.data.session_token) saveSessionToken(response.data.session_token);
      setUser(response.data);
      toast.success('Login realizado com sucesso!');
      setShowStudentLogin(false);
      // Check if student has a plan, redirect accordingly
      try {
        const planRes = await axios.get(`${API}/students/my-plan`);
        if (!planRes.data.plan) {
          navigate('/plans/student');
          return;
        }
      } catch (e) { console.error('Plan check error:', e); }
      navigate('/student');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código de acesso inválido');
    } finally {
      setLoading(false);
    }
  };

  const openLoginModal = () => {
    setFormData({ email: '', password: '' });
    setShowAuthModal(true);
  };

  React.useEffect(() => {
    if (skipRedirectRef.current) return;
    if (user && user.role) {
      const dashboardMap = { rh: '/rh', gym: '/gym', student: '/student', professional: '/professional' };
      navigate(dashboardMap[user.role]);
    } else if (user && !user.role) {
      navigate('/select-role');
    }
  }, [user, navigate]);

  const features = [
    { icon: <BarChart3 className="w-6 h-6" />, title: t('landing.feat1Title'), description: t('landing.feat1Desc') },
    { icon: <Activity className="w-6 h-6" />, title: t('landing.feat2Title'), description: t('landing.feat2Desc') },
    { icon: <TrendingUp className="w-6 h-6" />, title: t('landing.feat3Title'), description: t('landing.feat3Desc') },
    { icon: <Shield className="w-6 h-6" />, title: t('landing.feat4Title'), description: t('landing.feat4Desc') }
  ];

  const stats = [
    { value: "98%", label: t('landing.statSatisfaction') },
    { value: "50+", label: t('landing.statCompanies') },
    { value: "10k+", label: t('landing.statCollabs') },
    { value: "40%", label: t('landing.statAbsenteeism') },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-center" />
      
      {/* Header */}
      <header className="glass fixed top-0 w-full z-50 border-b border-slate-200/50 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 safe-x">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center gap-2">
              <Activity className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600" />
              <span className="text-lg sm:text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Vital
              </span>
            </div>
            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-3">
              <LanguageSwitcher />
              <a href="/admin/login" data-testid="admin-link">
                <Button variant="outline" className="border-slate-700 bg-slate-900 text-white hover:bg-slate-800 gap-2" size="sm">
                  <Shield className="w-4 h-4" />
                  {t('nav.admin')}
                </Button>
              </a>
              <Button 
                variant="outline"
                onClick={() => setShowStudentLogin(true)}
                data-testid="student-login-button"
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                {t('nav.student')}
              </Button>
              <Button 
                variant="outline"
                onClick={openLoginModal}
                data-testid="login-button"
                className="border-slate-200 text-slate-700 hover:bg-slate-50"
              >
                {t('nav.login')}
              </Button>
              <Button 
                onClick={() => setShowRegistrationModal(true)}
                data-testid="register-button"
                className="bg-slate-900 text-white hover:bg-slate-800 shadow-md transition-transform hover:-translate-y-0.5"
              >
                {t('nav.register')}
              </Button>
            </div>
            {/* Mobile: language + menu button */}
            <div className="md:hidden flex items-center gap-2">
              <LanguageSwitcher />
              <button 
                className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? <X className="w-6 h-6 text-slate-700" /> : <Menu className="w-6 h-6 text-slate-700" />}
              </button>
            </div>
          </div>
        </div>
        {/* Mobile nav menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 px-4 py-3 space-y-2 shadow-lg">
            <Button 
              variant="outline"
              onClick={() => { setShowStudentLogin(true); setMobileMenuOpen(false); }}
              data-testid="student-login-button-mobile"
              className="w-full justify-start border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            >
              <KeyRound className="w-4 h-4 mr-2" />
              {t('nav.student')}
            </Button>
            <Button 
              variant="outline"
              onClick={() => { openLoginModal(); setMobileMenuOpen(false); }}
              data-testid="login-button-mobile"
              className="w-full justify-start border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              <Lock className="w-4 h-4 mr-2" />
              {t('nav.login')}
            </Button>
            <Button 
              onClick={() => { setShowRegistrationModal(true); setMobileMenuOpen(false); }}
              data-testid="register-button-mobile"
              className="w-full justify-start bg-slate-900 text-white hover:bg-slate-800"
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              {t('nav.register')}
            </Button>
            <a href="/admin/login" data-testid="admin-link-mobile" className="block">
              <Button variant="ghost" className="w-full justify-start text-slate-500 hover:text-slate-700" size="sm">
                <Shield className="w-4 h-4 mr-2" />
                {t('nav.admin')}
              </Button>
            </a>
          </div>
        )}
      </header>

      {/* Login Modal */}
      <Dialog open={showAuthModal} onOpenChange={setShowAuthModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Activity className="w-5 h-5 text-blue-600" />
              Entrar
            </DialogTitle>
            <DialogDescription>
              Acesse sua conta de Empresa ou Academia
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAuthSubmit} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label htmlFor="auth-email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="auth-email"
                  data-testid="auth-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="seu@email.com"
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="auth-password">Senha</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="auth-password"
                  data-testid="auth-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Sua senha"
                  className="pl-10 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="text-right -mt-2">
              <button
                type="button"
                onClick={() => { setShowAuthModal(false); navigate('/reset-password'); }}
                className="text-sm text-blue-600 hover:underline"
                data-testid="forgot-password-link"
              >
                Esqueci minha senha
              </button>
            </div>

            {unverifiedEmail && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="unverified-email-warning">
                <div className="flex items-start gap-2">
                  <Mail className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">E-mail não verificado</p>
                    <p className="text-xs text-amber-700 mt-1">Verifique seu e-mail antes de fazer login. Não recebeu?</p>
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={resendingVerification}
                      data-testid="resend-verification-btn"
                      className="mt-2 text-xs font-semibold text-amber-900 underline hover:text-amber-700 disabled:opacity-50"
                    >
                      {resendingVerification ? 'Reenviando...' : 'Reenviar e-mail de verificação'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <Button
              type="submit"
              data-testid="auth-submit-button"
              disabled={loading}
              className="w-full bg-slate-900 hover:bg-slate-800"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Entrar
            </Button>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-500">ou</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleLogin}
              data-testid="google-login-button"
              className="w-full"
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continuar com Google
            </Button>

            <p className="text-center text-sm text-slate-500">
              Não tem conta? <button type="button" onClick={() => { setShowAuthModal(false); setShowRegistrationModal(true); }} className="text-blue-600 hover:underline font-medium" data-testid="switch-to-register">Criar conta</button>
            </p>
          </form>
        </DialogContent>
      </Dialog>

      {/* Registration Modal (multi-step) */}
      <RegistrationModal open={showRegistrationModal} onOpenChange={setShowRegistrationModal} />

      {/* Student Login Dialog */}
      <Dialog open={showStudentLogin} onOpenChange={setShowStudentLogin}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <KeyRound className="w-5 h-5 text-emerald-600" />
              Acesso do Aluno
            </DialogTitle>
            <DialogDescription>
              Digite o código de acesso fornecido pela sua empresa
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleStudentLogin} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="access-code">Código de Acesso</Label>
              <Input
                id="access-code"
                data-testid="access-code-input"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                placeholder="Ex: A1B2C3D4"
                className="text-center text-2xl tracking-widest font-mono uppercase"
                maxLength={8}
                autoComplete="off"
              />
            </div>
            
            <Button
              type="submit"
              data-testid="student-login-submit"
              disabled={loading || !accessCode.trim()}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <KeyRound className="w-4 h-4 mr-2" />
              )}
              Acessar
            </Button>
            
            <p className="text-xs text-slate-500 text-center">
              O código de acesso é fornecido pelo RH da sua empresa
            </p>
          </form>
        </DialogContent>
      </Dialog>

      {/* Hero Section */}
      <HeroSection 
        setShowRegistrationModal={setShowRegistrationModal}
        openLoginModal={openLoginModal}
        setKnowSection={setKnowSection}
      />

      {/* VITALPASS Walkthrough (below notebook image) */}
      <LandingVitalPassWalkthrough onCreateAccount={() => setShowRegistrationModal(true)} />

      {/* Stats Section */}
      <StatsSection stats={stats} />

      {/* AUDIENCE CARDS — 3 dedicated landing pages */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 lg:px-12 bg-slate-50" data-testid="audience-cards-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-sm font-bold uppercase tracking-widest text-blue-600 mb-2">DESCUBRA VITAL</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Qual é o seu perfil?
            </h2>
            <p className="text-slate-500 mt-3 max-w-xl mx-auto">
              Escolha a solução ideal para você e descubra como a Vital pode transformar sua rotina.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <button
              onClick={() => navigate('/para-empresa')}
              className="group text-left p-6 bg-white border-2 border-slate-100 hover:border-blue-500 rounded-2xl transition-all hover:shadow-xl hover:-translate-y-1"
              data-testid="audience-card-company"
            >
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4 group-hover:bg-blue-500 transition-colors">
                <Building2 className="w-6 h-6 text-blue-600 group-hover:text-white" />
              </div>
              <p className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-2">VITAL PARA</p>
              <h3 className="text-2xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>Sua Empresa</h3>
              <p className="text-sm text-slate-500 mb-4 leading-relaxed">Reduza absenteísmo e aumente a produtividade oferecendo saúde como benefício.</p>
              <span className="inline-flex items-center gap-1 text-sm font-bold text-blue-600 group-hover:gap-2 transition-all">
                Saiba mais <ArrowRight className="w-4 h-4" />
              </span>
            </button>

            <button
              onClick={() => navigate('/para-academia')}
              className="group text-left p-6 bg-white border-2 border-slate-100 hover:border-orange-500 rounded-2xl transition-all hover:shadow-xl hover:-translate-y-1"
              data-testid="audience-card-gym"
            >
              <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center mb-4 group-hover:bg-orange-500 transition-colors">
                <Dumbbell className="w-6 h-6 text-orange-600 group-hover:text-white" />
              </div>
              <p className="text-xs font-bold uppercase tracking-widest text-orange-500 mb-2">VITAL PARA</p>
              <h3 className="text-2xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>Sua Academia</h3>
              <p className="text-sm text-slate-500 mb-4 leading-relaxed">Conecte-se a milhares de novos alunos e gere receita recorrente.</p>
              <span className="inline-flex items-center gap-1 text-sm font-bold text-orange-500 group-hover:gap-2 transition-all">
                Saiba mais <ArrowRight className="w-4 h-4" />
              </span>
            </button>

            <button
              onClick={() => navigate('/para-voce')}
              className="group text-left p-6 bg-white border-2 border-slate-100 hover:border-emerald-500 rounded-2xl transition-all hover:shadow-xl hover:-translate-y-1"
              data-testid="audience-card-you"
            >
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mb-4 group-hover:bg-emerald-500 transition-colors">
                <Heart className="w-6 h-6 text-emerald-600 group-hover:text-white" />
              </div>
              <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-2">VITAL PARA</p>
              <h3 className="text-2xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>Você</h3>
              <p className="text-sm text-slate-500 mb-4 leading-relaxed">Escolha seu plano, sua academia e treine com liberdade total.</p>
              <span className="inline-flex items-center gap-1 text-sm font-bold text-emerald-600 group-hover:gap-2 transition-all">
                Saiba mais <ArrowRight className="w-4 h-4" />
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* CONHEÇA NOSSO SISTEMA */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 lg:px-12 bg-white" data-testid="know-our-system-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 tracking-tight mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {t('landing.knowSystem')}
            </h2>
            <p className="text-base text-slate-500">{t('landing.selectProfile')}</p>
          </div>

          {/* 3 Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-10" data-testid="know-buttons">
            <button onClick={() => setKnowSection(knowSection === 'empresa' ? null : 'empresa')}
              data-testid="btn-empresa"
              className={`group relative overflow-hidden rounded-2xl p-6 text-left transition-all border-2 ${knowSection === 'empresa' ? 'bg-blue-600 border-blue-600 text-white shadow-xl shadow-blue-200 scale-[1.02]' : 'bg-white border-slate-200 hover:border-blue-400 hover:shadow-lg'}`}>
              <Building2 className={`w-8 h-8 mb-3 ${knowSection === 'empresa' ? 'text-white' : 'text-blue-600'}`} />
              <p className={`font-black text-lg ${knowSection === 'empresa' ? 'text-white' : 'text-slate-900'}`} style={{ fontFamily: 'Chivo, sans-serif' }}>{t('landing.btnCompany')}</p>
              <p className={`text-xs mt-1 ${knowSection === 'empresa' ? 'text-blue-100' : 'text-slate-500'}`}>{t('landing.btnCompanyHint')}</p>
            </button>
            <button onClick={() => setKnowSection(knowSection === 'academia' ? null : 'academia')}
              data-testid="btn-academia"
              className={`group relative overflow-hidden rounded-2xl p-6 text-left transition-all border-2 ${knowSection === 'academia' ? 'bg-orange-500 border-orange-500 text-white shadow-xl shadow-orange-200 scale-[1.02]' : 'bg-white border-slate-200 hover:border-orange-400 hover:shadow-lg'}`}>
              <Dumbbell className={`w-8 h-8 mb-3 ${knowSection === 'academia' ? 'text-white' : 'text-orange-500'}`} />
              <p className={`font-black text-lg ${knowSection === 'academia' ? 'text-white' : 'text-slate-900'}`} style={{ fontFamily: 'Chivo, sans-serif' }}>{t('landing.btnGym')}</p>
              <p className={`text-xs mt-1 ${knowSection === 'academia' ? 'text-orange-100' : 'text-slate-500'}`}>{t('landing.btnGymHint')}</p>
            </button>
            <button onClick={() => setKnowSection(knowSection === 'voce' ? null : 'voce')}
              data-testid="btn-voce"
              className={`group relative overflow-hidden rounded-2xl p-6 text-left transition-all border-2 ${knowSection === 'voce' ? 'bg-emerald-600 border-emerald-600 text-white shadow-xl shadow-emerald-200 scale-[1.02]' : 'bg-white border-slate-200 hover:border-emerald-400 hover:shadow-lg'}`}>
              <Heart className={`w-8 h-8 mb-3 ${knowSection === 'voce' ? 'text-white' : 'text-emerald-600'}`} />
              <p className={`font-black text-lg ${knowSection === 'voce' ? 'text-white' : 'text-slate-900'}`} style={{ fontFamily: 'Chivo, sans-serif' }}>{t('landing.btnYou')}</p>
              <p className={`text-xs mt-1 ${knowSection === 'voce' ? 'text-emerald-100' : 'text-slate-500'}`}>{t('landing.btnYouHint')}</p>
            </button>
          </div>

          {/* Content */}
          {knowSection === 'empresa' && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-300 bg-gradient-to-br from-slate-50 to-blue-50 rounded-3xl p-4 sm:p-6 md:p-10 border border-blue-100" data-testid="content-empresa">
              <div className="text-center mb-8">
                <h3 className="text-2xl sm:text-3xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {t('landing.companyTitleA')} <span className="text-blue-600">VITAL</span> {t('landing.companyTitleB')}
                </h3>
                <p className="text-sm text-slate-500">{t('landing.companyTitleHint')}</p>
              </div>
              <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
                <table className="w-full" data-testid="pricing-table">
                  <thead>
                    <tr className="bg-slate-900 text-white">
                      <th className="px-4 sm:px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">{t('landing.tableRange')}</th>
                      <th className="px-4 sm:px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">{t('landing.tablePrice')}</th>
                      <th className="px-4 sm:px-6 py-4 text-center text-xs font-bold uppercase tracking-wider hidden sm:table-cell">{t('landing.tableExample')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { rangeKey: 6, price: 35, example: 6 },
                      { rangeKey: 15, price: 25, example: 15 },
                      { rangeKey: 30, price: 20, example: 30 },
                      { rangeKey: 50, price: 15, example: 50 },
                      { rangeKey: 100, price: 10, example: 100 },
                      { rangeKey: null, price: null, example: null },
                    ].map((tier) => {
                      const rangeLabel = tier.rangeKey
                        ? `${t('landing.tableUpTo')} ${tier.rangeKey} ${t('landing.tableCollabs')}`
                        : t('landing.tableAbove100');
                      return (
                      <tr key={rangeLabel} className={`border-b border-slate-100 hover:bg-blue-50/50 transition-colors`}>
                        <td className="px-4 sm:px-6 py-4 text-sm font-semibold text-slate-800">{rangeLabel}</td>
                        <td className="px-4 sm:px-6 py-4 text-center">
                          {tier.price ? (
                            <div>
                              <span className="text-lg sm:text-xl font-black text-blue-600">R$ {tier.price},00</span>
                              <p className="text-[10px] text-slate-400 sm:hidden mt-0.5">{tier.example}x = R$ {(tier.price * tier.example).toFixed(2).replace('.', ',')}/{t('landing.tableMonth')}</p>
                            </div>
                          ) : (
                            <span className="text-xs sm:text-sm text-slate-500 italic">{t('landing.tableOnRequest')}</span>
                          )}
                        </td>
                        <td className="px-4 sm:px-6 py-4 text-center hidden sm:table-cell">
                          {tier.example ? (
                            <span className="text-sm text-slate-600">{tier.example} {t('landing.tableEachCollab')} <strong className="text-slate-900">R$ {(tier.price * tier.example).toFixed(2).replace('.', ',')}</strong>/{t('landing.tableMonth')}</span>
                          ) : (
                            <span className="text-sm text-slate-500 italic">{t('landing.tableCustomAdmin')}</span>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-8 text-center">
                <p className="text-slate-600 text-sm mb-4">{t('landing.contactCta')}</p>
                <a href="mailto:contato@vitalperformance.app.br"
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-bold text-sm hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200">
                  <Mail className="w-4 h-4" />
                  contato@vitalperformance.app.br
                </a>
              </div>
            </div>
          )}

          {knowSection === 'academia' && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-300 bg-gradient-to-br from-slate-50 to-orange-50 rounded-3xl p-4 sm:p-6 md:p-10 border border-orange-100" data-testid="content-academia">
              <div className="mb-8">
                <p className="text-lg text-slate-700 leading-relaxed">
                  {t('landing.gymP1A')} <strong className="text-orange-600">VITAL</strong>{t('landing.gymP1B')} <strong>{t('landing.gymP1C')}</strong>{t('landing.gymP1D')}
                </p>
                <p className="text-base text-slate-600 mt-4 leading-relaxed">
                  {t('landing.gymP2')} <strong>{t('landing.gymP2B')}</strong> {t('landing.gymP2C')}
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-5 mb-8">
                <div className="bg-white rounded-2xl p-6 border border-orange-200 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center"><DollarSign className="w-5 h-5 text-orange-600" /></div>
                    <h4 className="font-black text-lg text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>{t('landing.gymCard1Title')}</h4>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {t('landing.gymCard1Body1')} <strong>{t('landing.gymCard1Body2')}</strong>{t('landing.gymCard1Body3')} <strong className="text-orange-600 text-lg">70%</strong> {t('landing.gymCard1Body4')}
                  </p>
                  <p className="text-sm text-slate-500 mt-3 italic">{t('landing.gymCard1Italic')}</p>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-orange-200 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-orange-600" /></div>
                    <h4 className="font-black text-lg text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>{t('landing.gymCard2Title')}</h4>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {t('landing.gymCard2Body1')} <strong className="text-orange-600 text-lg">30%</strong> {t('landing.gymCard2Body2')}
                  </p>
                  <p className="text-sm text-slate-500 mt-3 italic">{t('landing.gymCard2Italic')}</p>
                </div>
              </div>

              <div className="bg-orange-600 rounded-2xl p-5 sm:p-6 md:p-8 text-white text-center">
                <p className="text-base sm:text-lg font-bold mb-2">{t('landing.gymQuestion')}</p>
                <p className="text-sm sm:text-base opacity-90 mb-4 max-w-2xl mx-auto">
                  {t('landing.gymQuestionBody')} <strong>{t('landing.gymQuestionHighlight')}</strong>?
                </p>
                <p className="text-sm opacity-80 mb-5">
                  {t('landing.gymInvite')} <strong>{t('landing.gymInviteHighlight')}</strong> {t('landing.gymInviteEnd')}
                </p>
                <a href="mailto:contato@vitalperformance.app.br"
                  className="inline-flex items-center gap-2 bg-white text-orange-600 px-6 py-3 rounded-xl font-bold text-sm hover:bg-orange-50 transition-colors shadow-lg">
                  <Mail className="w-4 h-4" />
                  contato@vitalperformance.app.br
                </a>
              </div>
            </div>
          )}

          {knowSection === 'voce' && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-300 bg-gradient-to-br from-slate-50 to-emerald-50 rounded-3xl p-4 sm:p-6 md:p-10 border border-emerald-100" data-testid="content-voce">
              <div className="text-center mb-8">
                <h3 className="text-2xl sm:text-3xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {t('landing.youWelcome')} <span className="text-emerald-600">Vital Performance</span>
                </h3>
                <p className="text-sm text-slate-600 max-w-xl mx-auto">
                  {t('landing.youSubtitle')} <strong>{t('landing.youSubtitleHighlight')}</strong>
                </p>
                <p className="text-base text-slate-700 mt-4 font-semibold">{t('landing.youShow')}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { name: 'VITAL FIT', price: '89,90', color: 'emerald', key: 'fit', extra: 4 },
                  { name: 'VITAL PLUS', price: '129,90', color: 'blue', key: 'plus', extra: 7 },
                  { name: 'VITAL PREMIUM', price: '149,90', color: 'violet', key: 'premium', extra: 10 },
                  { name: 'VITAL GOLD', price: '169,90', color: 'amber', key: 'gold', extra: 12 },
                  { name: 'VITAL PLATINUM', price: '199,90', color: 'slate', key: 'platinum', extra: 14 },
                  { name: 'VITAL ACTION', price: '279,90', color: 'red', key: 'action', extra: 18 },
                ].map((plan) => {
                  const colorMap = {
                    emerald: { bg: 'bg-emerald-600', light: 'bg-emerald-50', text: 'text-emerald-600', ring: 'ring-emerald-200' },
                    blue: { bg: 'bg-blue-600', light: 'bg-blue-50', text: 'text-blue-600', ring: 'ring-blue-200' },
                    violet: { bg: 'bg-violet-600', light: 'bg-violet-50', text: 'text-violet-600', ring: 'ring-violet-200' },
                    amber: { bg: 'bg-amber-500', light: 'bg-amber-50', text: 'text-amber-600', ring: 'ring-amber-200' },
                    slate: { bg: 'bg-slate-800', light: 'bg-slate-100', text: 'text-slate-700', ring: 'ring-slate-300' },
                    red: { bg: 'bg-red-600', light: 'bg-red-50', text: 'text-red-600', ring: 'ring-red-200' },
                  };
                  const c = colorMap[plan.color];
                  const features = t(`landing.plans.${plan.key}.features`) || [];
                  return (
                    <div key={plan.name} className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg transition-all hover:-translate-y-1" data-testid={`know-plan-${plan.name}`}>
                      <div className={`${c.bg} px-5 py-3 flex items-center justify-between`}>
                        <span className="text-white font-black text-sm tracking-wide" style={{ fontFamily: 'Chivo, sans-serif' }}>{plan.name}</span>
                        <span className="text-white/90 text-xs font-bold">R$ {plan.price}/{t('landing.tableMonth')}</span>
                      </div>
                      <div className="p-5">
                        <ul className="space-y-2 mb-4">
                          {(Array.isArray(features) ? features : []).map((f, j) => (
                            <li key={j} className="flex items-start gap-2 text-xs text-slate-600">
                              <CheckCircle2 className={`w-3.5 h-3.5 ${c.text} mt-0.5 flex-shrink-0`} />
                              {f}
                            </li>
                          ))}
                        </ul>
                        <div className={`${c.light} rounded-lg px-3 py-2 flex items-center gap-2`}>
                          <Zap className={`w-4 h-4 ${c.text}`} />
                          <span className={`text-xs font-bold ${c.text}`}>{t('landing.planExtra')} {plan.extra} {t('landing.planExtraSuffix')}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 space-y-4 text-center">
                <p className="text-sm text-emerald-700 font-semibold bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-3 inline-block">
                  {t('landing.dependent')}
                </p>
                <div className="bg-white rounded-2xl p-5 border border-slate-200 max-w-lg mx-auto">
                  <p className="font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>{t('landing.notLocked')}</p>
                  <div className="flex flex-col items-start gap-1.5 text-sm text-slate-600 mx-auto w-fit">
                    <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> {t('landing.trainBase')}</span>
                    <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> {t('landing.explore')}</span>
                    <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> {t('landing.freedom')}</span>
                  </div>
                </div>
                <p className="text-xs text-slate-400 italic">{t('landing.priceNote')}</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-3 sm:mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {t('landing.featuresTitle')}
            </h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              {t('landing.featuresSubtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {features.map((feature) => (
              <div 
                key={feature.title}
                className="bg-white border border-slate-100 rounded-xl p-6 card-hover"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600 mb-3 sm:mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-sm sm:text-lg font-bold text-slate-900 mb-1 sm:mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Profiles Section */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-3 sm:mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {t('landing.profilesTitle')}
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-8">
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100">
              <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-6">
                <Building2 className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {t('landing.profileHR')}
              </h3>
              <ul className="space-y-3">
                {(t('landing.profileHRItems') || []).map((item) => (
                  <li key={item} className="flex items-start gap-2 text-slate-600 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100">
              <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center mb-6">
                <Activity className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {t('landing.profileGym')}
              </h3>
              <ul className="space-y-3">
                {(t('landing.profileGymItems') || []).map((item) => (
                  <li key={item} className="flex items-start gap-2 text-slate-600 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100">
              <div className="w-14 h-14 bg-emerald-500 rounded-xl flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {t('landing.profileStudent')}
              </h3>
              <ul className="space-y-3">
                {(t('landing.profileStudentItems') || []).map((item) => (
                  <li key={item} className="flex items-start gap-2 text-slate-600 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 lg:px-12 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight mb-4 sm:mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Comece a Monitorar a Saúde do Seu Time
          </h2>
          <p className="text-base sm:text-lg text-slate-300 mb-6 sm:mb-8 max-w-2xl mx-auto">
            Junte-se a empresas que já transformaram seu programa de bem-estar com dados e inteligência artificial.
          </p>
          <Button 
            onClick={() => setShowRegistrationModal(true)}
            data-testid="cta-bottom-button"
            size="lg"
            className="bg-white text-slate-900 hover:bg-slate-100 shadow-lg px-8"
          >
            Criar Conta Grátis
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-10 px-4 sm:px-6 lg:px-12 bg-slate-900 border-t border-slate-800" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
            {/* Logo + Nome */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Activity className="w-7 h-7 text-blue-400" />
                <span className="text-white font-bold text-lg tracking-wide" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  VITAL PERFORMANCE HUB
                </span>
              </div>
              <p className="text-slate-500 text-xs uppercase tracking-widest">
                2026 <span aria-label="Copyright">©</span> Todos os direitos reservados
              </p>
            </div>

            {/* Informações Corporativas */}
            <div className="text-sm text-slate-400 space-y-2" data-testid="footer-company-info">
              <div>
                <p className="text-slate-500 text-[10px] uppercase tracking-widest mb-1">CNPJ</p>
                <p>67.919.347/0001-03</p>
              </div>
              <div>
                <p className="text-slate-500 text-[10px] uppercase tracking-widest mb-1">Endereço</p>
                <p>Rua Rio Grande do Norte, 1435<br />Belo Horizonte - MG · 30130-138</p>
              </div>
            </div>

            {/* Contato + Admin */}
            <div className="text-sm text-slate-400 space-y-2">
              <div>
                <p className="text-slate-500 text-[10px] uppercase tracking-widest mb-1">Telefone</p>
                <a href="tel:+5541978801450" className="hover:text-blue-400 transition-colors">
                  (41) 9788-0145
                </a>
              </div>
              <div>
                <p className="text-slate-500 text-[10px] uppercase tracking-widest mb-1">E-mail</p>
                <a href="mailto:contato@vitalperformance.app.br" className="hover:text-blue-400 transition-colors break-all">
                  contato@vitalperformance.app.br
                </a>
              </div>
              <div className="pt-2">
                <a href="/admin/login" className="text-slate-600 hover:text-slate-400 text-xs transition-colors" data-testid="admin-link">
                  Área Admin
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
